/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Wed Jan 10 2024
 **/

import { useQuery, useQueryClient } from "@tanstack/react-query";

import { EventID, GROUP_POPOVER_ID, ModalID, ZIndex } from "@/frameworks/core/commons";
import { useFileManager, usePopover, useConfirm } from "@/frameworks/core/hooks";
import { Avatar, Divider, Menu, Row, Typography, useModal, useToast } from "@/frameworks/designStuff/components";
import { Maybe, SwitchCase } from "@/frameworks/libraries/components";
import { EventHandler } from "@rfice/eventhandler";
import { Configure } from "@/frameworks/libraries/utilities";
import { _T, _P } from "@rfice/language";
import { useProperties } from "@/frameworks/libraries/properties";
import { hoverScrollCss } from "@/frameworks/designStuff/utils";

import { FileMoveModal } from "../FileMoveModal";
import { useFileSelect } from "../hooks";
import { SpaceStatusChip } from "../../../../../components/Folders/private/emotion";
import { useDeleteFile } from "../hooks";

export default function FileMoreMenu({
	file,
	onClickMenu,
	isShare3D
}: {
	file: Required<FileBox.Folders.GetFolderFileListResponse>["content"][number];
	onClickMenu: (ev: React.MouseEvent) => void;
	isShare3D: boolean;
}) {
	const { currentFolder, getFolderFileList } = useFileManager();
	const popover = usePopover();
	const modal = useModal();
	const { downloadFile, downloadFolderZip } = useFileSelect(file);
	const { confirm } = useConfirm();
	const deleteFileOrFolder = useDeleteFile({ enableToast: false });

	const { data: totalElements = 1 } = useQuery({
		queryKey: [
			"folderFileList",
			{
				id: file.id,
				referenceId: file.referenceId,
				referenceType: file.referenceType
			}
		],
		queryFn: async () => {
			const result = await getFolderFileList({
				id: file.id,
				referenceId: file.referenceId,
				referenceType: file.referenceType
			});
			return result.totalElements;
		},
		refetchOnWindowFocus: false
	});

	const handleClickMenu = (ev: React.MouseEvent) => {
		const id = ev.currentTarget.id as "rename" | "move" | "download" | "delete" | "copy";

		switch (id) {
			case "rename": {
				EventHandler.emit(EventID.UPDATE_FILE_NAME, file);
				break;
			}
			case "move": {
				modal.show({
					id: ModalID.MOVE_FILE,
					content: <FileMoveModal file={file} />,
					zIndex: ZIndex.FILEBOX + 1
				});
				break;
			}
			case "delete": {
				confirm(_P("{{folderName}}을 삭제하시겠습니까?", { folderName: file.name }), {
					onConfirm() {
						deleteFileOrFolder({ id: file.id, fileBoxType: file.fileBoxType });
					}
				});

				break;
			}
			case "download": {
				if (file.fileBoxType === "FILE") {
					downloadFile();
				}
				if (file.fileBoxType === "FOLDER") {
					downloadFolderZip();
				}
				break;
			}
			case "copy": {
				popover.show({
					popoverID: GROUP_POPOVER_ID.FILE_MORE_MENU,
					uniqueID: "copy",
					anchorEl: ev.currentTarget,
					content: <CopyTargetFolderList {...file} onCopySuccess={onClickMenu} />,
					zIndex: ZIndex.FILEBOX + 1,
					direction: { x: "right", y: "top" }
				});
				return;
			}
		}

		onClickMenu(ev);
	};

	const isDMChatFile = file.referenceType === "CHAT";
	const isSpaceChatFile = file.referenceType === "SPACE" && file.parentReferenceType === "CHAT_ROOM";

	return (
		<Menu css={{ background: "#fff" }}>
			<Menu.List>
				<Maybe test={file.fileBoxType !== "FOLDER"}>
					<Menu.Item id="copy" onClick={handleClickMenu}>
						<Typography typoSize="Title3" typoColor="neutral_80_100">
							{_T("사본 만들기")}
						</Typography>
					</Menu.Item>
				</Maybe>
				<Maybe test={Configure.userID === file.createBy && !isDMChatFile && !isSpaceChatFile}>
					<SwitchCase
						condition={file.fileBoxType}
						cases={{
							FILE: (
								<Menu.Item id="rename" onClick={handleClickMenu}>
									<Typography typoSize="Title3" typoColor="neutral_80_100">
										{_T("파일명 변경")}
									</Typography>
								</Menu.Item>
							),
							FOLDER: (
								<Menu.Item id="rename" onClick={handleClickMenu}>
									<Typography typoSize="Title3" typoColor="neutral_80_100">
										{_T("폴더명 변경")}
									</Typography>
								</Menu.Item>
							)
						}}
					/>
				</Maybe>
				<Maybe test={currentFolder.isMyFile && !file.callRecordFolder}>
					<Menu.Item id="move" onClick={handleClickMenu}>
						<Typography typoSize="Title3" typoColor="neutral_80_100">
							<Maybe test={file.fileBoxType === "FOLDER"} fallback={_T("폴더로 이동")}>
								{_T("이동")}
							</Maybe>
						</Typography>
					</Menu.Item>
				</Maybe>
				<Maybe test={file.fileBoxType === "FOLDER" && totalElements > 0}>
					<Menu.Item id="download" onClick={handleClickMenu}>
						<Typography typoSize="Title3" typoColor="neutral_80_100">
							{_T("다운로드")}
						</Typography>
					</Menu.Item>
				</Maybe>
				<Maybe test={Configure.userID === file.createBy && !isShare3D}>
					<div css={{ width: "100%", padding: "6px 0" }}>
						<Divider type="line" />
					</div>
					<Menu.Item id="delete" onClick={handleClickMenu}>
						<Typography typoSize="Title3" typoColor="warning_80_100">
							{_T("삭제")}
						</Typography>
					</Menu.Item>
				</Maybe>
			</Menu.List>
		</Menu>
	);
}

function CopyTargetFolderList(
	props: Required<FileBox.Folders.GetFolderFileListResponse>["content"][number] & {
		onCopySuccess: (ev: React.MouseEvent<HTMLLIElement>) => void;
	}
) {
	const { copyFile, copyLink, currentFolder, myRoomID } = useFileManager();
	const queryClient = useQueryClient();
	const toast = useToast();
	const properties = useProperties();

	const handleClick = (
		ev: React.MouseEvent<HTMLLIElement>,
		requestParams: Omit<FileBox.Files.CopyFileRequest, "id"> & { folderName: string }
	) => {
		let request = copyFile;
		if (props.fileBoxType === "LINK") request = copyLink;
		request({
			id: props.id,
			folderId: requestParams.folderId,
			referenceType: requestParams.referenceType,
			referenceId: requestParams.referenceId
		}).then(() => {
			queryClient.refetchQueries({ queryKey: ["folderFileList", { id: requestParams.folderId }] });
			toast.show({
				type: "system",
				message: _T("{{to}}으로 {{file}}이 복사되었습니다.", { to: requestParams.folderName, file: props.name })
			});
			props.onCopySuccess(ev);
		});
	};

	const allFolders = queryClient.getQueryData<SwaggerFileAPIModels.ApiChatSimpleRoomIdResponse>(["all_folders"]);

	return (
		<Menu css={[{ background: "#fff" }]}>
			<Menu.List
				maxHeight="320px"
				css={[{ display: "flex", flexDirection: "column", gap: "8px" }, hoverScrollCss()]}
			>
				<Maybe
					test={currentFolder.referenceType !== "CHAT" && typeof currentFolder.referenceType !== "undefined"}
				>
					<Menu.Item
						onClick={(ev) =>
							handleClick(ev, {
								folderId: currentFolder.id,
								referenceId: currentFolder.referenceID || currentFolder.id,
								referenceType: currentFolder.referenceType || "CHAT",
								folderName: currentFolder.name ?? ""
							})
						}
					>
						<Typography typoSize="Body3_MD">{_T("현재위치")}</Typography>
					</Menu.Item>
				</Maybe>
				<Maybe test={!currentFolder.isMyFile}>
					<Menu.Item
						onClick={(ev) =>
							handleClick(ev, {
								folderId: myRoomID,
								referenceId: myRoomID,
								referenceType: "INDIVIDUAL",
								folderName: _T("내 파일")
							})
						}
					>
						<Typography typoSize="Body3_MD">{_T("내 파일")}</Typography>
					</Menu.Item>
				</Maybe>
				<div css={{ width: "100%", padding: "6px 0" }}>
					<Divider type="line" />
				</div>
				<Typography typoSize="Body5_SB">{_T("스페이스 파일함")}</Typography>
				{allFolders?.spaces?.map((space) => (
					<Menu.Item
						key={space.spaceId}
						onClick={(ev) => {
							handleClick(ev, {
								folderId: space.myRoom ? myRoomID : space.spaceId,
								referenceId: space.myRoom ? myRoomID : space.spaceId,
								referenceType: space.myRoom ? "INDIVIDUAL" : "SPACE",
								folderName: space.myRoom ? _T("마이룸") : space.spaceName || ""
							});
						}}
					>
						<Row gap={12}>
							<Avatar
								type="image"
								kind="people_24"
								text={space.spaceName || ""}
								src={`${properties.default.webDomain}${space.thumbnailPath}`}
							/>
							<Maybe
								test={space.myRoom}
								fallback={
									<Typography
										align="left"
										ellipsis
										maxLine={1}
										typoColor="neutral_80_100"
										typoSize="Body3_MD"
										css={{ maxWidth: 116 }}
									>
										{space.spaceName}
									</Typography>
								}
							>
								<Typography typoColor="neutral_80_100" typoSize="Body3_MD">
									{_T("마이룸")}
								</Typography>
							</Maybe>
							{space.deleted && (
								<SpaceStatusChip status="deleted" typoSize="Body7" typoColor="neutral_00_100">
									{_T("삭제됨")}
								</SpaceStatusChip>
							)}
							{allFolders.lastSpaceId === space.spaceId && (
								<SpaceStatusChip status="participated" typoSize="Body7" typoColor="neutral_00_100">
									{_T("접속중")}
								</SpaceStatusChip>
							)}
						</Row>
					</Menu.Item>
				))}
			</Menu.List>
		</Menu>
	);
}
